import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "motion/react";
import { whatsappLink } from "@/mocks/llkContent";

export default function ExitPopup() {
  const [isOpen, setIsOpen] = useState(false);
  const [hasShown, setHasShown] = useState(false);

  useEffect(() => {
    // Check if already shown in this session
    const shown = sessionStorage.getItem("llk-exit-popup-shown");
    if (shown) {
      setHasShown(true);
      return;
    }

    // Desktop: mouse leaving the viewport from top
    const handleMouseLeave = (e: MouseEvent) => {
      if (e.clientY < 10 && !hasShown) {
        setIsOpen(true);
        setHasShown(true);
        sessionStorage.setItem("llk-exit-popup-shown", "true");
      }
    };

    // Mobile: detect scroll up past threshold or back gesture attempt
    let lastScrollY = window.scrollY;
    const handleScroll = () => {
      const currentScrollY = window.scrollY;
      // User scrolled up significantly after being deep in the page
      if (currentScrollY < lastScrollY && currentScrollY < 200 && lastScrollY > 800 && !hasShown) {
        setIsOpen(true);
        setHasShown(true);
        sessionStorage.setItem("llk-exit-popup-shown", "true");
      }
      lastScrollY = currentScrollY;
    };

    // Also show after 45 seconds on mobile if user hasn't scrolled much
    const mobileTimer = setTimeout(() => {
      const isMobile = window.innerWidth < 768;
      if (isMobile && !hasShown && window.scrollY > 300) {
        setIsOpen(true);
        setHasShown(true);
        sessionStorage.setItem("llk-exit-popup-shown", "true");
      }
    }, 45000);

    document.addEventListener("mouseleave", handleMouseLeave);
    window.addEventListener("scroll", handleScroll, { passive: true });

    return () => {
      document.removeEventListener("mouseleave", handleMouseLeave);
      window.removeEventListener("scroll", handleScroll);
      clearTimeout(mobileTimer);
    };
  }, [hasShown]);

  const closePopup = () => {
    setIsOpen(false);
  };

  if (hasShown && !isOpen) return null;

  return (
    <AnimatePresence>
      {isOpen && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.3 }}
          className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm"
          onClick={closePopup}
        >
          <motion.div
            initial={{ opacity: 0, scale: 0.9, y: 30 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.9, y: 30 }}
            transition={{ duration: 0.4, ease: [0.25, 0.1, 0.25, 1] }}
            className="relative w-full max-w-md sm:max-w-lg bg-white rounded-2xl sm:rounded-3xl shadow-2xl overflow-hidden"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Top accent bar */}
            <div className="h-1.5 w-full bg-gradient-to-r from-llk-gold via-llk-gold-light to-llk-gold" />

            {/* Close button */}
            <button
              onClick={closePopup}
              className="absolute top-4 right-4 w-8 h-8 flex items-center justify-center rounded-full bg-llk-cream text-llk-dark hover:bg-llk-dark hover:text-white transition-colors z-10 cursor-pointer"
            >
              <i className="ri-close-line text-lg" />
            </button>

            <div className="p-6 sm:p-8 text-center">
              {/* Icon */}
              <div className="w-14 h-14 sm:w-16 sm:h-16 rounded-full bg-llk-gold/10 flex items-center justify-center mx-auto mb-4 sm:mb-5">
                <i className="ri-plant-line text-2xl sm:text-3xl text-llk-gold" />
              </div>

              {/* Headline */}
              <h3 className="font-display text-xl sm:text-2xl md:text-3xl text-llk-dark font-semibold mb-2 sm:mb-3 leading-snug">
                Espere! Sua lavoura ainda pode ir mais longe
              </h3>

              {/* Subheadline */}
              <p className="text-llk-dark/60 text-sm sm:text-base leading-relaxed mb-5 sm:mb-6 max-w-sm mx-auto">
                Antes de sair, receba uma orientação gratuita sobre qual solução biológica da LLK faz mais sentido para sua cultura e momento produtivo.
              </p>

              {/* Benefits bullets */}
              <ul className="flex flex-col gap-2 sm:gap-2.5 mb-6 sm:mb-7 text-left max-w-xs mx-auto">
                {[
                  "Análise rápida da sua situação",
                  "Indicação da tecnologia certa",
                  "Sem compromisso",
                ].map((item, idx) => (
                  <li key={idx} className="flex items-center gap-2.5 text-sm text-llk-dark/80">
                    <div className="w-5 h-5 rounded-full bg-llk-green-soft/20 flex items-center justify-center shrink-0">
                      <i className="ri-check-line text-llk-green-soft text-xs" />
                    </div>
                    {item}
                  </li>
                ))}
              </ul>

              {/* CTA */}
              <a
                href={whatsappLink}
                target="_blank"
                rel="noopener noreferrer"
                onClick={closePopup}
                className="inline-flex items-center justify-center gap-2 w-full sm:w-auto px-6 sm:px-8 py-3.5 sm:py-4 bg-llk-dark text-white text-sm sm:text-base font-semibold rounded-full hover:bg-llk-gold transition-colors whitespace-nowrap"
              >
                <i className="ri-whatsapp-line text-lg" />
                Falar com Especialista Agora
              </a>

              {/* Secondary close */}
              <button
                onClick={closePopup}
                className="block mx-auto mt-3 sm:mt-4 text-llk-dark/40 text-xs sm:text-sm hover:text-llk-dark/70 transition-colors cursor-pointer"
              >
                Prefiro continuar navegando
              </button>
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}